import type { PartnerPromoCode } from '@backmarket/front-office/api/partner-promo-code/types'

import type {
  BuybackOffer,
  Country,
  InsuranceOfferWithSelectedState,
  LinkInternal,
  MonetaryAmount,
  Price,
  Url,
  collectionPointsAPI,
} from '@backmarket/http-api'

import type { MobilePlanOffer } from '../../api-specs-b2c-services/mobile-plan/types/mobile-plan-offers'

export type Deliver = {
  birthdate: string | null
  city: string | null
  company: string | null
  country: Country | '' | null
  countryDialInCode: string | null
  customerIdNumber: string | null
  dateCreation: string | null
  dateModification: string | null
  email: string | null
  firstName: string | null
  firstNamePronunciation: string | null
  lastName: string | null
  lastNamePronunciation: string | null
  phone: string | null
  postalCode: string | null
  stateOrProvince: string | null
  street: string | null
  street2: string | null
  warnings: string[]
}

export type Bill = {
  birthdate: string | null
  city: string | null
  company: string | null
  country: Country | '' | null
  countryDialInCode: string | null
  customerIdNumber: string | null
  dateCreation: string | null
  dateModification: string | null
  email: string | null
  firstName: string | null
  firstNamePronunciation: string | null
  lastName: string | null
  lastNamePronunciation: string | null
  phone: string | null
  postalCode: string | null
  stateOrProvince: string | null
  street: string | null
  street2: string | null
}

export type CollectionPoint = {
  id: string | null
  name: string | null
  address: collectionPointsAPI.CollectionPointAddress
}

export type DeliverCollectionPoint = {
  collectionPoint: CollectionPoint
  firstName: string
  lastName: string | null
  phone: string | null
  countryDialInCode: string
}

export function isDeliverCollectionPoint(
  address: Deliver | DeliverCollectionPoint,
): address is DeliverCollectionPoint {
  return (address as DeliverCollectionPoint)?.collectionPoint !== undefined
}

export type AddressList = {
  deliver: Deliver
  bill: Bill
  deliverCollectionPoint?: DeliverCollectionPoint
}

export type ShippableCountry = {
  name: string
  countryCode: string
}

export type BillableCountry = ShippableCountry

export type CartSummary = {
  /**
   * @deprecated Cart total delivery cost. It is deprecated and `totalDelivery` should be used instead
   */
  deliveryTotal: string
  /**
   * Cart total delivery price with currency, as a string
   */
  deliveryTotalWithCurrency: string
  /**
   * Cart total price without delivery with currency, as a string
   */
  totalPriceWithoutDeliveryWithCurrency: string
  /**
   * Cart subtotal including listing price and shipping price
   */
  totalGrossPriceWithoutAddonService?: MonetaryAmount
  /**
   * Cart subtotal including listing price ,shipping price and insurance price
   */
  totalGrossPriceWithoutBmFee?: MonetaryAmount
  totalPriceWithoutAddonService?: MonetaryAmount
  /**
   * Cart total price only for listings
   */
  totalListingsPrice: MonetaryAmount
  totalDelivery: MonetaryAmount
  /**
   * Cart total price
   */
  totalPrice: string
  /**
   * Whether the total price includes sales tax, or not. Can be `null` if the market country has no separate sales tax (such as FR).
   */
  totalPriceIncludesTax: boolean | null
  /**
   * Cart total price with currency, as a string
   */
  totalPriceWithCurrency: string
  /**
   * Cart total quantity of items
   */
  totalQuantity: number
  /**
   * Cart contains insurance subscription
   */
  insuranceSubscription: boolean
  /**
   * @deprecated Country taxes value. Deprecated, prefer using tax and totalPriceIncludesTax fields instead.
   */
  countryTaxes: number
  /**
   * @deprecated Country taxes value with currency, as a string. Deprecated, prefer using tax and totalPriceIncludesTax fields instead.
   */
  countryTaxesWithCurrency: string
  /**
   * Additional fee applied to the entire cart (optional)
   */
  serviceFee?: MonetaryAmount
  /**
   * Sales tax. Can be `null` if the market country has no separate sales tax (such as FR), or if the tax amount is not known yet (such as US when shipping address is unknown).
   */
  tax: MonetaryAmount | null
  /**
   * Cart total price included buyback discount
   */
  totalPriceAfterBuyback?: MonetaryAmount
}

export type SwapAd = {
  /**
   * Cart price with swap discount with currency
   */
  cartPriceWithSwapDiscountWithCurrency: string
  /**
   * Cart price with swap discount
   */
  cartPriceWithSwapDiscount: number
  /**
   * Amount discounted with currency
   */
  swapDiscountWithCurrency: string
  /**
   * Amount discounted
   */
  swapDiscount: number
}

export type Swap = {
  offer: BuybackOffer | null
  /**
   * reminder of the information displayed on the PP for the swap advertisement
   */
  ad: SwapAd
}

export type SelectedShipping = {
  shippingFeePk: number
  shippingFeePrice: string
  shippingFeePriceWithCurrency: string
  sumShippingFeePrice: string
  sumShippingFeePriceWithCurrency: string
  shippingFeeDelay: number
  shippingDelayLabel: string
  shipperDisplay: string
  shipperId: number
  shippingId: string
}

export type Warranty = {
  type: string
  delay: number
  price: number
  priceWithCurrency: string
  cgv: boolean
  selected: boolean
}

export type ShippingChoice = {
  type: string
  currency: string
  priceWithCurrency: string
  price: number
  title: string
  freeWarranty?: boolean
  shippingDelay?: number
  shippingDelayLabel?: string
  earliestArrivalDate?: string
  latestArrivalDate?: string
  shipperDisplay?: string
  shipperId?: number
  shippingId: string
  selected: boolean
  isCollectionPoint?: boolean
}

export type ShippingOption = {
  title: string
  modal: string
  type: string
  appearance?: string
  choices: ShippingChoice[]
}

export type InstallmentPlan = {
  annualPercentageRate: number
  monthlyPrice: Price
  numberOfMonths: number
}

export type PromoCode = {
  eligible: boolean
  token: string
  discountAmount: MonetaryAmount
  errorType?: string
}

export type CartItem = {
  listingId: string
  listingPublicId: string
  sku?: string
  title: string
  quantity: number
  quantityMax: number
  productPageUrl: Url
  link: LinkInternal
  image: Url
  gradeId: number
  gradeName: string | null
  gradeDescription: string | string[]
  gradeExtended: { hasNewBattery: boolean }
  price: number
  priceWithCurrency: string
  sumPrice: string
  sumPriceWithCurrency: string
  formerPrice: number
  formerPriceWithCurrency: string
  shippingSelected: SelectedShipping | Record<string, never>
  warranties: Warranty[]
  specialOfferType: number
  available: string
  backlabel: Record<string, never> | null
  options: ShippingOption[]
  insuranceOffers: InsuranceOfferWithSelectedState[]
  countryTax: number
  productId: string
  akeneoId: string
  brand: string
  marketplaceCategoryId: number
  category: string
  categoryId: number
  color: string
  model: string
  merchant: string
  merchantId: number
  merchantPublicId: string
  dateAddToBag: string
  installmentMonthlyPaymentPlan: InstallmentPlan | null
  mobilePlan: {
    selectedOffer: MobilePlanOffer
  } | null
  partnerPromoCode: PartnerPromoCode | null
  existsNew: boolean
  promoCodes?: PromoCode[]
}

export type SearchAddress = {
  street: string | null
  street2: string | null
  postalCode: string | null
  city: string | null
  country: string | null
}

export type CartCollectionPoint = {
  id: string
  name: string
  address: CollectionPoint
}

export type LastCollectionPoint = {
  search: { address: SearchAddress }
  selectedPoint: CartCollectionPoint
}

export type UserInformation = {
  missingData: boolean
  formType: 'INSURANCE_ONLY_FORM' | 'NONE'
  birthdate?: string
  taxId?: string
}

export type CartResponse = {
  /**
   * Delivery address and billing address of the current user
   */
  addressList: AddressList
  /**
   * Cart is elligible to paypal
   */
  paypalMerchant: boolean
  /**
   * Countries we can ship.
   */
  shippableCountries: ShippableCountry[]
  /**
   * Countries we can bill.
   */
  billableCountries: BillableCountry[]
  /**
   *  Summary of the cart
   */
  summary: CartSummary
  /**
   * Swap details
   */
  swap: Swap
  cartItems: CartItem[]
  /**
   * The collection point object that a user has selected during Checkout, together with the search address that the user used to find this collection point.
   */
  lastCollectionPoint?: LastCollectionPoint | Record<string, never>
  userInformation: UserInformation
  isInsuranceARBoostAvailable: boolean
}
